/**
 * useInitialLanguageIndex
 *
 * Owns the language selector's OPENING scroll position: the index — within
 * the ordered options from `useLanguageOptions` — of the language that was
 * active when the sheet opened, frozen on the first render.
 *
 * Why frozen: `VirtualList`'s `initialIndex` describes the list's opening
 * position, so it must NOT follow `locale`. The sheet deliberately stays open
 * after a pick (the user watches the title re-render in the chosen language),
 * and a live value would ask the list to re-scroll to the row just tapped.
 * The `useState` lazy initialiser is the freeze: it runs on mount only, and
 * the setter is intentionally discarded.
 *
 * Why `undefined` rather than the raw index: `VirtualList` runs a
 * `visibility: hidden` measurement phase for any defined `initialIndex`
 * (`VirtualList.tsx:1088-1094`, `:1394-1399`). Index `0` needs no scroll and
 * `-1` (locale absent from `options`) has nothing to scroll to, so both are
 * mapped to `undefined` and the list opens on its plain path.
 *
 * Lives here rather than in `LanguageSelectorContent` because "which row is
 * the active locale" is derived from the option ordering `useLanguageOptions`
 * owns (`conventions.md` §"New responsibility → new owner file"), mirroring
 * `useSendGift.ts:71` → `SendGiftSheetContent.tsx:134`. Kept separate from
 * `useLanguageOptions` so that hook's return contract — and its test — stay
 * unchanged.
 *
 * `options` is a parameter rather than a second `useLanguageOptions()` call so
 * the ordering is computed once, by its one owner, and both hooks observe the
 * same array.
 *
 * **Web-only — there is no Flutter counterpart** (no picker, no localization
 * layer in the Flutter app).
 */

import { useState } from 'react';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import type { LocaleOption } from '@/presentation/locale';

export const useInitialLanguageIndex = (options: LocaleOption[]): number | undefined => {
  const { locale } = useLocale();

  const [initialIndex] = useState<number | undefined>(() => {
    const index = options.findIndex(option => option.code === locale);
    return index > 0 ? index : undefined;
  });

  return initialIndex;
};
